import React, { useState, useCallback, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  getParties,
  getPartyDetails,
  deleteParty,
} from "../../services/services";
import PartyDetails from "./partyDetails";
import moment from "moment";
import Loading from "../../components/loading";
import SearchAndFilter from "../items/SearchAndFilter";
import Pagination from "../../components/Pagination";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import AddParty from "./addParty";
import { confirmDelete } from "../../components/confirmDelete";
import { Link } from "react-router-dom";
import SearchFilter from "../purchase/SearchFilter";

const Party = () => {
  const user = JSON.parse(localStorage.getItem("Login_User")) || {};
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [activePanel, setActivePanel] = useState(null);
  const navigate = useNavigate();

  const categories = ["customer", "supplier"];

  const fetchApi = useCallback(
    async (filters = {}, page = 1) => {
      setLoading(true);
      const payload = {
        search: filters.name ?? "",
        mobile: filters.mobile ?? "",
        from: filters.from ?? "",
        to: filters.to ?? "",
        partyType: filters.partyType ?? "",
        page,
        limit: itemsPerPage,
      };
      try {
        const response = await getParties(payload);
        if (response.status && response.data?.response) {
          const res = response.data.response;
          const pagination = response.data;
          setParties(res.parties || []);
          setCurrentPage(pagination.page || page);
          setTotalPages(pagination.totalPages || 1);
          setTotalItems(pagination.total || 0);
        } else {
          setParties([]);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalItems(0);
        }
      } catch {
        setParties([]);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage],
  );
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  useEffect(() => {
    fetchApi(
      {
        name: searchTerm,
        partyType: filterType === "all" ? "" : filterType,
        from: dateRange.from,
        to: dateRange.to,
      },
      1,
      itemsPerPage,
    );
  }, [searchTerm, filterType, dateRange, itemsPerPage]);

  const handlePartySelect = async (party) => {
    // setSelectedPartyId(party._id);
    // setActivePanel("details");
    // try {
    //   const response = await getPartyDetails(party._id);
    //   const { party: fullParty, balance, balanceType, ledger } = response.data;
    //   setSelectedParty({ ...fullParty, balance, balanceType, ledger });
    // } catch {}

    navigate(`/party/details/${party._id}`);
  };

  const handleAddParty = () => navigate("/party/addParty");

  const handleEditParty = (id) => {
    navigate(`/party/edit/${id}`);
  };
  const handleDeleteParty = async (id, name, party) => {
    const isConfirm = await confirmDelete(`party ${name}`);
    if (!isConfirm) return;

    if (party?.ledger?.length > 0) {
      toast.error("Cannot delete party. Transactions exist.");
      return;
    }

    try {
      setLoading(true);

      const res = await deleteParty(id);

      if (res?.data?.status) {
        toast.success(res.data.message || "Party deleted successfully");

        fetchApi(
          {
            name: searchTerm,
            partyType: filterType === "all" ? "" : filterType,
            from: dateRange.from,
            to: dateRange.to,
          },
          currentPage,
        );
      } else {
        toast.error(
          res?.data?.message ||
            "Cannot delete party. Sales or transactions exist.",
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Cannot delete party. Sales or transactions exist.";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  /* ===== DataTable Columns ===== */
  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
        grow: 1.5,
      },
      {
        name: "Mobile",
        selector: (row) => row.mobile || "—",
        width: "10rem",
      },
      {
        name: "Email",
        cell: (row) => row.email || "—",
        width: "15rem",
      },
      {
        name: "Amount (₹)",
        cell: (row) => (
          <span
            className={`fw-bold ${
              row.balance === 0
                ? "text-muted"
                : row.balanceType === "You'll Give"
                  ? "text-danger"
                  : "text-success"
            }`}
          >
            {Math.abs(row.balance || 0).toFixed(2)}
          </span>
        ),
        width: "8rem",
      },
      {
        name: "Category",
        selector: (row) => row.partyType || "—",
        style: {
          textTransform: "capitalize",
        },
      },
      {
        name: "Date",
        selector: (row) => moment(row.date).format("DD MMM, YYYY"),
        width: "10rem",
      },
      {
        name: "Actions",
        // center: true,
        width: "110px",
        cell: (row) => (
          <>
            <button
              className="btn btn-sm btn-light border text-primary me-1"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEditParty(row._id);
              }}
            >
              <i className="bi bi-pencil"></i>
            </button>
            <button
              className="btn btn-sm btn-light border text-danger"
              title="Delete"
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteParty(row._id, row.name);
              }}
            >
              <i className="bi bi-trash"></i>
            </button>
          </>
        ),
      },
    ],
    [selectedParty],
  );

  return (
    <div id="main">
      <div className="d-flex align-items-center justify-content-between mb-2 gap-3 flex-column flex-md-row">
        <div className="d-flex align-items-center justify-content-between w-100">
          <h4 className="custom-title mb-0 text-start">Manage Party</h4>

          {/* Mobile Button */}
          <Link to="/party/addParty" className="d-block d-md-none">
            <button className="btn btn-primary p-2 text-nowrap">
              Add Party
            </button>
          </Link>
        </div>
        <div className="d-flex align-items-center gap-2 ">
          {/* Search */}
          <SearchFilter
            showItemName={false}
            onSearch={({ query }) => {
              // setPage(1);

              if (query?.fromDate) {
                setDateRange({
                  from: query.fromDate,
                  to: query.toDate,
                });
                setSearchTerm("");
              } else if (query?.searchBy) {
                const parsed = JSON.parse(query.searchBy);

                setSearchTerm(parsed.name || parsed.itemName || "");
                setDateRange({ from: "", to: "" });
              } else {
                setSearchTerm("");
                setDateRange({ from: "", to: "" });
              }
            }}
          />
          {/* Desktop Button */}
          <button
            className="btn btn-primary p-2 text-nowrap d-none d-md-block"
            onClick={() => navigate("/party/addParty")}
          >
            Add Party
          </button>
        </div>
      </div>

      <div className="col-lg-12 mt-3">
        <div className="card">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={parties}
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={<Loading />}
              noDataComponent={
                <div className="py-4 text-muted text-center">
                  <i className="bi bi-inbox fs-1 mb-2 d-block"></i>
                  No Party Found
                </div>
              }
              highlightOnHover
              striped
              onRowClicked={handlePartySelect}
              // fixedHeader
              // fixedHeaderScrollHeight="550px"
            />
          </div>
        </div>
        {parties.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={itemsPerPage}
            totalItems={totalItems}
            loading={loading}
            onPageChange={(page) =>
              fetchApi(
                {
                  name: searchTerm,
                  partyType: filterType === "all" ? "" : filterType,
                  from: dateRange.from,
                  to: dateRange.to,
                },
                page,
                itemsPerPage,
              )
            }
            onLimitChange={(newLimit) => {
              setItemsPerPage(newLimit);
            }}
          />
        )}
      </div>

      {activePanel === "details" && selectedParty && (
        <div
          className="position-fixed end-0 top-0 h-100 bg-light p-0"
          style={{
            width: "40rem",
            zIndex: 1030,
          }}
        >
          {/* Close Button */}
          <div className="d-flex justify-content-between align-items-center card-header bg-custom text-light ">
            <h5 className="mb-0">Party Details</h5>
            <button
              className="btn"
              onClick={() => {
                setActivePanel(null);
                setSelectedParty(null);
                setSelectedPartyId(null);
              }}
            >
              <i className="bi bi-x-lg text-white"></i>
            </button>
          </div>

          {/* Party Details Component */}
          <div className="px-3 shadow h-100">
            <PartyDetails
              selectedParty={selectedParty}
              onEdit={(id) => {
                navigate(`/party/edit/${id}`);
              }}
            />
          </div>
        </div>
      )}

      {/* Overlay when sidebar open (optional, for mobile) */}
      {activePanel === "details" && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-lg-none"
          style={{ zIndex: 1029 }}
          onClick={() => setActivePanel(null)}
        />
      )}
    </div>
  );
};

export default headerLayoutWrapper(Party);
